import { useArchiveConfirmationModal } from '@/components/ConfirmationModal';
import { TErrorNotification } from '@/components/ErrorInlineNotification/ErrorInlineNotification';
import { BusinessDocumentDetail } from '@/context/services/business_document/type';
import {
  useBusinessDocumentArchiveCallback,
  useBusinessDocumentRestoreCallback,
  useCallBusinessDocumentArchive,
  useCallRestoreBusinessDocument,
} from '@/features/business_documents/Detail/components/Right/hooks/useBusinessDocumentDetail';
import { useMakeCopyIdURL } from '@/hooks/useMakeCopyURL';
import { useTranslation } from '@/i18n';
import { isString } from '@/libs/typeguard/string';
import { useNotification } from '@moneyforward/ap-frontend-components';

import { useCallback } from 'react';

export const useBusinessDocumentButtonActions = (
  data: BusinessDocumentDetail,
  setError: TErrorNotification['setError']
) => {
  const { t } = useTranslation();
  const notify = useNotification();
  const modal = useArchiveConfirmationModal();
  const copyIdUrl = useMakeCopyIdURL(`${data.id}`);

  const onCancelClick = useCallback(() => {
    modal.close();
  }, [modal]);

  const handleArchiveCallback = useBusinessDocumentArchiveCallback(
    modal,
    notify
  );

  const onHandleArchive = useCallBusinessDocumentArchive(
    data.id!,
    handleArchiveCallback,
    setError
  );

  const onArchiveClick = useCallback(() => {
    modal.open({
      title: t('business_document_archive_confirmation_title'),
      bodyTitle: t('archive_modal_content_business_document'),
      fileName: data.filename ?? '',
      pics: data.pics?.map((item) => item.name).filter(isString) || [],
      onCancelClick: onCancelClick,
      onArchiveClick: onHandleArchive,
      size: 'md',
    });
  }, [modal, data.filename, data.pics, onCancelClick, onHandleArchive, t]);

  const handleRestoreArchiveCallback =
    useBusinessDocumentRestoreCallback(notify);

  const onRestoreArchiveClick = useCallRestoreBusinessDocument(
    data.id!,
    handleRestoreArchiveCallback,
    setError
  );

  return {
    onArchiveClick,
    onRestoreArchiveClick,
    copyIdUrl,
  };
};
